import { Type } from "@themes/pollinator/ui/Type";
import { Image } from "@themes/pollinator/ui/Image";

export type Props = {
  /** Profile image URL */
  image?: string;
  /** Full name */
  name: string;
  /** Pronouns (optional) */
  pronouns?: string;
  /** Test ID */
  "data-testid"?: string;
};

/**
 * Profile header with avatar, name, and pronouns.
 * Displays the profile image with a decorative border, name, and optional pronouns.
 */
export function ProfileHeader({
  image,
  name,
  pronouns,
  "data-testid": testId,
}: Props) {
  return (
    <div
      className="flex flex-col gap-2 p-6 border-b border-brand-800"
      data-testid={testId}
    >
      {/* Avatar with decorative border */}
      <div
        className="relative w-24 h-24 rounded-full border border-brand-800 bg-brand-200 p-1"
        data-testid={testId ? `${testId}-avatar` : "profile-header-avatar"}
      >
        {image ? (
          <Image
            src={image}
            alt={name}
            className="w-full h-full rounded-full overflow-hidden"
            imageClassName="object-cover shadow-xl"
          />
        ) : (
          <div className="w-full h-full rounded-full bg-brand-800 flex items-center justify-center">
            <Type variant="h4" className="text-brand-50">
              {name.charAt(0).toUpperCase()}
            </Type>
          </div>
        )}
      </div>

      {/* Name and pronouns */}
      <Type
        as="h2"
        variant="h4"
        className="tracking-[-0.4px]"
        data-testid={testId ? `${testId}-name` : "profile-header-name"}
      >
        {name}
      </Type>
      {pronouns && (
        <Type
          variant="caption"
          data-testid={testId ? `${testId}-pronouns` : "profile-header-pronouns"}
        >
          {pronouns}
        </Type>
      )}
    </div>
  );
}

export default ProfileHeader;
